import "@/App.css";
import { useState, Suspense } from "react";
import { lazyRetry } from "@/lib/lazyRetry";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { SuperAdminProvider, useSuperAdmin } from "@/contexts/SuperAdminContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { LangProvider } from "@/contexts/LangContext";
import { Sidebar } from "@/components/Sidebar";
import { AskStoreWidget } from "@/components/ai/AskStoreWidget";
import { SuperAdminLayout } from "@/components/SuperAdminLayout";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ModuleGate } from "@/components/ModuleGate";
import { ConfirmProvider } from "@/components/ConfirmDialog";
import CustomDomainShop from "@/pages/CustomDomainShop";
import { isCustomDomainActive } from "@/lib/customDomain";
import { initErrorLogger } from "@/lib/errorLogger";
import { LogOut, ArrowLeft } from "lucide-react";

initErrorLogger();

// Critical path
import LoginPage from "@/pages/LoginPage";

// Lazy-loaded tenant pages (Dashboard pulls in recharts — keep it out of the login/POS bundle)
const DashboardPage = lazyRetry(() => import("@/pages/DashboardPage"));
const POSPage = lazyRetry(() => import("@/pages/POSPage"));
const InventoryPage = lazyRetry(() => import("@/pages/InventoryPage"));
const ShipmentsPage = lazyRetry(() => import("@/pages/ShipmentsPage"));
const StaffHRPage = lazyRetry(() => import("@/pages/StaffHRPage"));
const MyWorkPage = lazyRetry(() => import("@/pages/MyWorkPage"));
const AiStudioPage = lazyRetry(() => import("@/pages/AiStudioPage"));
const ReportsPage = lazyRetry(() => import("@/pages/ReportsPage"));
const CustomersPage = lazyRetry(() => import("@/pages/CustomersPage"));
const SettingsPage = lazyRetry(() => import("@/pages/SettingsPage"));
const ShopCustomizerPage = lazyRetry(() => import("@/pages/ShopCustomizerPage"));
const MessengerPage = lazyRetry(() => import("@/pages/MessengerPage"));
const AuditLogPage = lazyRetry(() => import("@/pages/AuditLogPage"));
const ShopGateway = lazyRetry(() => import("@/pages/ShopGateway"));
const OrderTrackingPage = lazyRetry(() => import("@/pages/OrderTrackingPage"));
const OrdersPage = lazyRetry(() => import("@/pages/OrdersPage"));
const ReturnsPage = lazyRetry(() => import("@/pages/ReturnsPage"));
const WhatsAppOrdersPage = lazyRetry(() => import("@/pages/WhatsAppOrdersPage"));
const SocialPage = lazyRetry(() => import("@/pages/SocialPage"));
const VendorsPage = lazyRetry(() => import("@/pages/VendorsPage"));
const AnalyticsPage = lazyRetry(() => import("@/pages/AnalyticsPage"));
const StoresPage = lazyRetry(() => import("@/pages/StoresPage"));
const SetupWizard = lazyRetry(() => import("@/pages/SetupWizard"));
const LoyaltyPage = lazyRetry(() => import("@/pages/LoyaltyPage"));
const WhatsAppPage = lazyRetry(() => import("@/pages/WhatsAppPage"));
const CouponsPage = lazyRetry(() => import("@/pages/CouponsPage"));
const CustomerDisplayPage = lazyRetry(() => import("@/pages/CustomerDisplayPage"));
const CatalogManagerPage = lazyRetry(() => import("@/pages/CatalogManagerPage"));
const CatalogPage = lazyRetry(() => import("@/pages/CatalogPage"));
const CatalogProductPage = lazyRetry(() => import("@/pages/CatalogProductPage"));

// Super Admin pages
const SuperAdminLoginPage = lazyRetry(() => import("@/pages/superadmin/SuperAdminLoginPage"));
const SuperAdminDashboard = lazyRetry(() => import("@/pages/superadmin/SuperAdminDashboard"));
const TenantManagementPage = lazyRetry(() => import("@/pages/superadmin/TenantManagementPage"));
const TenantSignupPage = lazyRetry(() => import("@/pages/superadmin/TenantSignupPage"));
const PlansManagementPage = lazyRetry(() => import("@/pages/superadmin/PlansManagementPage"));
const TemplatesManagementPage = lazyRetry(() => import("@/pages/superadmin/TemplatesManagementPage"));
const ActivityLogsPage = lazyRetry(() => import("@/pages/superadmin/AuditLogsPage"));
const SignupRequestsPage = lazyRetry(() => import("@/pages/superadmin/SignupRequestsPage"));
const SubscriptionsPage = lazyRetry(() => import("@/pages/superadmin/SubscriptionsPage"));
const TrialsPage = lazyRetry(() => import("@/pages/superadmin/TrialsPage"));
const TenantUsersPage = lazyRetry(() => import("@/pages/superadmin/TenantUsersPage"));
const ModuleAccessPage = lazyRetry(() => import("@/pages/superadmin/ModuleAccessPage"));
const SystemSettingsPage = lazyRetry(() => import("@/pages/superadmin/SystemSettingsPage"));
const SupportNotesPage = lazyRetry(() => import("@/pages/superadmin/SupportNotesPage"));
const ReceiveTransferPage = lazyRetry(() => import("@/pages/ReceiveTransferPage"));

import { PWAInstallBanner, PWAUpdateBanner, OfflineIndicator } from "@/components/PWAComponents";
import { ConnectivityProvider } from "@/contexts/ConnectivityContext";

const PageSpinner = () => (
  <div className="min-h-[50vh] flex items-center justify-center">
    <div className="w-7 h-7 border-2 border-[#2563EB] border-t-transparent rounded-full animate-spin" />
  </div>
);

const FullSpinner = ({ color = "#2563EB" }) => (
  <div className="min-h-screen flex items-center justify-center bg-th-bg">
    <div className="w-8 h-8 border-2 rounded-full animate-spin" style={{ borderColor: color, borderTopColor: "transparent" }} />
  </div>
);

function ProtectedRoute({ children, roles }) {
  const { user, loading, setupCompleted } = useAuth();
  const location = useLocation();
  if (loading) return <FullSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!setupCompleted && location.pathname !== "/setup" && user.role_code === "tenant_owner") return <Navigate to="/setup" replace />;
  if (roles && !roles.includes(user.role_code)) return <Navigate to="/" replace />;
  return children;
}

function BackButton() {
  const navigate = useNavigate();
  const location = useLocation();
  if (location.pathname === "/") return null;
  return (
    <button onClick={() => navigate(-1)} data-testid="global-back-button"
      className="group flex items-center gap-1.5 px-3 py-1.5 -ml-1 mb-3 rounded-lg text-th-muted hover:text-cream-900 hover:bg-th-surface border border-transparent hover:border-th-border transition-all text-sm font-medium">
      <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
      <span>Back</span>
    </button>
  );
}

function AppLayout({ children }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const location = useLocation();
  const isPOS = location.pathname === "/pos";
  return (
    <div className="flex h-screen bg-th-bg overflow-hidden">
      <Sidebar onCollapseChange={setSidebarCollapsed} />
      <main className={`flex-1 transition-all duration-300 min-w-0 flex flex-col overflow-hidden pwa-safe-top ${sidebarCollapsed ? "md:ml-14" : "md:ml-64"} ${isPOS ? "p-2 md:p-3 pb-16 md:pb-3" : "p-3 md:p-6 pb-20 md:pb-6 overflow-y-auto"}`}>
        <BackButton />{children}
      </main>
      <AskStoreWidget />
    </div>
  );
}

function StaffLayout({ children }) {
  const { user, logout } = useAuth();
  return (
    <div className="flex flex-col min-h-screen bg-th-bg pwa-safe-top">
      <header className="h-14 bg-th-card border-b border-th-border flex items-center justify-between px-6 flex-shrink-0">
        <div className="flex items-center gap-3">
          <img src="/logo_compact.png" alt="AR POS" className="w-8 h-8 rounded-lg object-cover" />
          <span className="font-heading text-lg text-cream-900">AR POS SYSTEM</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-th-muted">{user?.name}</span>
          <button onClick={logout} data-testid="staff-logout-button" className="p-2 rounded-lg text-th-dim hover:text-clay-500 hover:bg-th-surface transition-colors">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>
      <main className="flex-1 p-4 overflow-hidden">{children}</main>
    </div>
  );
}

function AuthRedirect({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullSpinner />;
  if (user) return <Navigate to={user.role_code === "cashier" ? "/pos" : "/"} replace />;
  return children;
}

function StaffOrAdminRoute({ staffElement, adminElement }) {
  const { user } = useAuth();
  if (user?.role_code === "cashier") return staffElement;
  return adminElement;
}

function SuperAdminProtectedRoute({ children }) {
  const { admin, loading } = useSuperAdmin();
  if (loading) return <FullSpinner color="#6366f1" />;
  if (!admin) return <Navigate to="/platform/login" replace />;
  return children;
}

function SuperAdminAuthRedirect({ children }) {
  const { admin, loading } = useSuperAdmin();
  if (loading) return <FullSpinner color="#6366f1" />;
  if (admin) return <Navigate to="/platform" replace />;
  return children;
}

function Lazy({ children }) {
  return <ErrorBoundary><Suspense fallback={<PageSpinner />}>{children}</Suspense></ErrorBoundary>;
}

function SAPage({ children }) {
  return <SuperAdminProtectedRoute><SuperAdminLayout><Lazy>{children}</Lazy></SuperAdminLayout></SuperAdminProtectedRoute>;
}

function AppRoutes() {
  if (isCustomDomainActive()) return <CustomDomainShop />;
  return (
    <Routes>
      {/* SuperAdmin Routes */}
      <Route path="/platform/login" element={<SuperAdminAuthRedirect><Lazy><SuperAdminLoginPage /></Lazy></SuperAdminAuthRedirect>} />
      <Route path="/platform" element={<SAPage><SuperAdminDashboard /></SAPage>} />
      <Route path="/platform/tenants" element={<SAPage><TenantManagementPage /></SAPage>} />
      <Route path="/platform/signup-requests" element={<SAPage><SignupRequestsPage /></SAPage>} />
      <Route path="/platform/tenant-users" element={<SAPage><TenantUsersPage /></SAPage>} />
      <Route path="/platform/plans" element={<SAPage><PlansManagementPage /></SAPage>} />
      <Route path="/platform/subscriptions" element={<SAPage><SubscriptionsPage /></SAPage>} />
      <Route path="/platform/trials" element={<SAPage><TrialsPage /></SAPage>} />
      <Route path="/platform/templates" element={<SAPage><TemplatesManagementPage /></SAPage>} />
      <Route path="/platform/modules" element={<SAPage><ModuleAccessPage /></SAPage>} />
      <Route path="/platform/activity-logs" element={<SAPage><ActivityLogsPage /></SAPage>} />
      <Route path="/platform/support-notes" element={<SAPage><SupportNotesPage /></SAPage>} />
      <Route path="/platform/settings" element={<SAPage><SystemSettingsPage /></SAPage>} />

      {/* Public */}
      <Route path="/signup" element={<Lazy><TenantSignupPage /></Lazy>} />
      <Route path="/login" element={<AuthRedirect><LoginPage /></AuthRedirect>} />
      <Route path="/shop/:tenantSlug/*" element={<Lazy><ShopGateway /></Lazy>} />
      <Route path="/catalog/:tenantSlug" element={<Lazy><CatalogPage /></Lazy>} />
      <Route path="/catalog/product/:productSlug" element={<Lazy><CatalogProductPage /></Lazy>} />
      <Route path="/catalog/:tenantSlug/product/:productId" element={<Lazy><CatalogProductPage /></Lazy>} />
      <Route path="/receive-transfer/:slug/:token" element={<Lazy><ReceiveTransferPage /></Lazy>} />
      <Route path="/track/:tenantSlug/:orderNumber" element={<Lazy><OrderTrackingPage /></Lazy>} />

      {/* Tenant App Routes */}
      <Route path="/setup" element={<ProtectedRoute roles={["tenant_owner"]}><Lazy><SetupWizard /></Lazy></ProtectedRoute>} />
      <Route path="/" element={
        <ProtectedRoute>
          <StaffOrAdminRoute staffElement={<Navigate to="/pos" replace />} adminElement={<AppLayout><Lazy><DashboardPage /></Lazy></AppLayout>} />
        </ProtectedRoute>
      } />
      <Route path="/pos" element={<ProtectedRoute><AppLayout><ModuleGate moduleKey="pos"><Lazy><POSPage /></Lazy></ModuleGate></AppLayout></ProtectedRoute>} />
      <Route path="/pos/display" element={<ProtectedRoute><Lazy><CustomerDisplayPage /></Lazy></ProtectedRoute>} />
      <Route path="/inventory" element={<ProtectedRoute roles={["tenant_owner","manager","inventory_staff"]}><AppLayout><ModuleGate moduleKey="inventory"><Lazy><InventoryPage /></Lazy></ModuleGate></AppLayout></ProtectedRoute>} />
      <Route path="/shipments" element={<ProtectedRoute roles={["tenant_owner","manager","inventory_staff"]}><AppLayout><ModuleGate moduleKey="shipments"><Lazy><ShipmentsPage /></Lazy></ModuleGate></AppLayout></ProtectedRoute>} />
      <Route path="/customers" element={<ProtectedRoute><AppLayout><ModuleGate moduleKey="customers"><Lazy><CustomersPage /></Lazy></ModuleGate></AppLayout></ProtectedRoute>} />
      <Route path="/loyalty" element={<ProtectedRoute><AppLayout><ModuleGate moduleKey="loyalty"><Lazy><LoyaltyPage /></Lazy></ModuleGate></AppLayout></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><AppLayout><ModuleGate moduleKey="messages"><Lazy><MessengerPage /></Lazy></ModuleGate></AppLayout></ProtectedRoute>} />
      <Route path="/staff" element={<ProtectedRoute roles={["tenant_owner","manager"]}><AppLayout><ModuleGate moduleKey="staff"><Lazy><StaffHRPage /></Lazy></ModuleGate></AppLayout></ProtectedRoute>} />
      <Route path="/my-work" element={<ProtectedRoute><AppLayout><Lazy><MyWorkPage /></Lazy></AppLayout></ProtectedRoute>} />
      <Route path="/ai-studio" element={<ProtectedRoute roles={["tenant_owner","manager"]}><AppLayout><Lazy><AiStudioPage /></Lazy></AppLayout></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute roles={["tenant_owner","manager"]}><AppLayout><ModuleGate moduleKey="reports"><Lazy><ReportsPage /></Lazy></ModuleGate></AppLayout></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute roles={["tenant_owner","manager"]}><AppLayout><ModuleGate moduleKey="analytics"><Lazy><AnalyticsPage /></Lazy></ModuleGate></AppLayout></ProtectedRoute>} />
      <Route path="/audit-log" element={<ProtectedRoute roles={["tenant_owner"]}><AppLayout><ModuleGate moduleKey="audit_log"><Lazy><AuditLogPage /></Lazy></ModuleGate></AppLayout></ProtectedRoute>} />
      <Route path="/orders" element={<ProtectedRoute roles={["tenant_owner","manager"]}><AppLayout><ModuleGate moduleKey="shop"><Lazy><OrdersPage /></Lazy></ModuleGate></AppLayout></ProtectedRoute>} />
      <Route path="/returns" element={<ProtectedRoute roles={["tenant_owner","manager","cashier"]}><AppLayout><Lazy><ReturnsPage /></Lazy></AppLayout></ProtectedRoute>} />
      <Route path="/whatsapp-orders" element={<ProtectedRoute roles={["tenant_owner","manager"]}><AppLayout><ModuleGate moduleKey="whatsapp"><Lazy><WhatsAppOrdersPage /></Lazy></ModuleGate></AppLayout></ProtectedRoute>} />
      <Route path="/social" element={<ProtectedRoute roles={["tenant_owner"]}><AppLayout><ModuleGate moduleKey="social"><Lazy><SocialPage /></Lazy></ModuleGate></AppLayout></ProtectedRoute>} />
      <Route path="/vendors" element={<ProtectedRoute roles={["tenant_owner","manager"]}><AppLayout><ModuleGate moduleKey="suppliers"><Lazy><VendorsPage /></Lazy></ModuleGate></AppLayout></ProtectedRoute>} />
      <Route path="/shop" element={<ProtectedRoute roles={["tenant_owner","manager"]}><AppLayout><ModuleGate moduleKey="shop"><Lazy><ShopGateway /></Lazy></ModuleGate></AppLayout></ProtectedRoute>} />
      <Route path="/shop-customizer" element={<ProtectedRoute roles={["tenant_owner","manager"]}><AppLayout><ModuleGate moduleKey="shop"><Lazy><ShopCustomizerPage /></Lazy></ModuleGate></AppLayout></ProtectedRoute>} />
      <Route path="/catalog" element={<ProtectedRoute roles={["tenant_owner","manager"]}><AppLayout><ModuleGate moduleKey="shop"><Lazy><CatalogManagerPage /></Lazy></ModuleGate></AppLayout></ProtectedRoute>} />
      <Route path="/shop-preview/*" element={<ProtectedRoute roles={["tenant_owner","manager"]}><Lazy><ShopGateway designPreview /></Lazy></ProtectedRoute>} />
      <Route path="/stores" element={<ProtectedRoute roles={["tenant_owner"]}><AppLayout><Lazy><StoresPage /></Lazy></AppLayout></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute roles={["tenant_owner"]}><AppLayout><Lazy><SettingsPage /></Lazy></AppLayout></ProtectedRoute>} />
      <Route path="/branding" element={<ProtectedRoute roles={["tenant_owner"]}><Navigate to="/settings" replace /></ProtectedRoute>} />
      <Route path="/whatsapp" element={<ProtectedRoute roles={["tenant_owner","manager"]}><AppLayout><ModuleGate moduleKey="whatsapp"><Lazy><WhatsAppPage /></Lazy></ModuleGate></AppLayout></ProtectedRoute>} />
      <Route path="/coupons" element={<ProtectedRoute roles={["tenant_owner","manager"]}><AppLayout><ModuleGate moduleKey="coupons"><Lazy><CouponsPage /></Lazy></ModuleGate></AppLayout></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LangProvider>
      <ConnectivityProvider>
      <ThemeProvider>
        <ConfirmProvider>
        <SuperAdminProvider>
        <AuthProvider>
          <AppRoutes />
          <PWAInstallBanner />
          <PWAUpdateBanner />
          <OfflineIndicator />
          <Toaster position="top-right" toastOptions={{
            style: {
              background: 'var(--th-card)', border: '1px solid var(--th-border)',
              borderRadius: '10px', fontFamily: 'Inter, -apple-system, sans-serif',
              fontSize: '14px', color: 'var(--th-text)',
            },
          }} />
        </AuthProvider>
        </SuperAdminProvider>
        </ConfirmProvider>
      </ThemeProvider>
      </ConnectivityProvider>
      </LangProvider>
    </BrowserRouter>
  );
}

export default App;
